import React, { useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepButton,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';

const FormStepper = ({
  steps,
  activeStep,
  setActiveStep
}: {
  steps: string[],
  activeStep: number,
  setActiveStep: (step: number) => void
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const activeStepRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current && activeStepRef.current) {
      const container = scrollRef.current;
      const active = activeStepRef.current;
      const targetScroll = active.offsetLeft - container.clientWidth / 2 + active.clientWidth / 2;
      container.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }
  }, [activeStep]);

  return (
    <Paper
      elevation={2}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        bgcolor: 'background.paper',
        borderRadius: 0,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Scroll container — hides scrollbar, scrollRef lives here */}
      <Box
        ref={scrollRef}
        sx={{
          overflowX: 'auto',
          overflowY: 'visible',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          px: 2,
          py: 1.5,
        }}
      >
        <Stepper
          nonLinear
          activeStep={activeStep}
          sx={{
            minWidth: 'max-content', // never wraps — container scrolls instead
            '& .MuiStepLabel-label': {
              fontSize: isMobile ? '0.7rem' : '0.8rem',
              whiteSpace: 'nowrap',
              mt: 0.5,
            },
            '& .MuiStepButton-root': {
              py: 0.75,
              px: isMobile ? 0.75 : 1.5,
              borderRadius: 1,
              transition: 'background-color 0.2s',
            },
            '& .MuiStepIcon-root': { color: '#e0e0e0' },
            '& .MuiStepIcon-text': { fill: '#1e293b' },
          }}
        >
          {steps.map((label, index) => (
            <Step
              key={label}
              ref={index === activeStep ? activeStepRef : null}
            >
              <StepButton
                onClick={() => setActiveStep(index)}
                sx={{
                  bgcolor: index === activeStep ? 'rgba(79,195,247,0.12)' : 'transparent',
                  transform: index === activeStep ? 'scale(1.05)' : 'none',
                  '& .MuiStepLabel-label': {
                    fontWeight: index === activeStep ? 700 : 400,
                    color: index === activeStep ? '#4FC3F7' : 'inherit',
                  },
                  '& .MuiStepIcon-root': {
                    color: index === activeStep ? '#4FC3F7' : undefined,
                  },
                }}
              >
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Paper>
  );
};

export default FormStepper;
